import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, GitCompare } from "lucide-react";
import { useCompare } from "../contexts/CompareContext";

/**
 * Small floating navigation control ("<  >") shown right under the main
 * Navbar on every internal page. Lets users step back and forward through
 * their browsing history without hunting for a back button on each
 * screen. Hidden on the home page and inside admin routes where a full
 * back/forward would be disorienting for ops.
 *
 * We use `window.history.length` (falls back on 1 when unavailable) to
 * decide whether the "forward" chevron is meaningful — the browser
 * doesn't expose a "can I go forward?" API, so the best heuristic is to
 * track our own last-forward flag: when the user goes Back we remember
 * that Forward is available; any subsequent navigation to a new URL
 * (i.e. not a history nav) wipes that flag.
 */
export default function PageBackNav() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, key } = useLocation();
  const [canGoForward, setCanGoForward] = useState(false);
  const { count: compareCount } = useCompare();

  // Every time the pathname changes, we can't tell if the change came
  // from a Back/Forward click or a fresh navigation — so we conservatively
  // clear the "can forward" flag after any pathname change. The user's
  // click on our Back button will re-enable it below.
  useEffect(() => {
    setCanGoForward(false);
    // key changes on every navigation; pathname might not (query change)
  }, [pathname, key]);

  // Hide on the root and on screens where the navbar already provides a
  // richer "close" affordance (checkout modals, admin panels use tabs).
  const hidden =
    pathname === "/" ||
    pathname === "" ||
    pathname.startsWith("/admin") ||
    pathname.startsWith("/login");

  if (hidden) return null;

  const handleBack = () => {
    // Remember that Forward is now meaningful, THEN navigate. The state
    // update is picked up before the render triggered by navigation.
    setCanGoForward(true);
    navigate(-1);
  };

  const handleForward = () => navigate(1);

  return (
    <div
      className="sticky top-16 z-30 border-b border-neutral-200/70 bg-white/85 backdrop-blur-md"
      data-testid="page-back-nav"
    >
      <div className="container-main flex items-center gap-1 h-10">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-sm font-medium text-neutral-700 hover:bg-neutral-100 active:scale-95 transition-all"
          aria-label={t("backNav.back")}
          data-testid="page-back-btn"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline">{t("backNav.back")}</span>
        </button>
        <button
          type="button"
          onClick={handleForward}
          disabled={!canGoForward}
          className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-sm font-medium text-neutral-700 hover:bg-neutral-100 active:scale-95 transition-all disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"
          aria-label={t("backNav.forward")}
          data-testid="page-forward-btn"
        >
          <span className="hidden sm:inline">{t("backNav.forward")}</span>
          <ChevronRight className="w-4 h-4" />
        </button>

        {/* Compare chip — appears once the user has ⭐ at least one car.
            Tapping opens the /comparar page with the side-by-side view. */}
        {compareCount > 0 && pathname !== "/comparar" && (
          <Link
            to="/comparar"
            className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-amber-400 text-amber-950 text-xs font-semibold shadow-sm hover:bg-amber-300 active:scale-95 transition-all"
            data-testid="compare-chip"
          >
            <GitCompare className="w-3.5 h-3.5" />
            {t("backNav.compare")} ({compareCount})
          </Link>
        )}
      </div>
    </div>
  );
}
